<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Check, ChevronsUpDown } from 'lucide-vue-next'
import { cn } from '@/lib/utils'

const props = defineProps<{
  templateId: number
}>()

const emit = defineEmits<{
  (e: 'template-updated'): void
}>()

const isOpen = ref(false)
const isProcessing = ref(false)
const isLoadingProjects = ref(false)
const projects = ref<Array<{ project_id: number; project_name: string; organization_name: string }>>([])
const selectedProjectIds = ref<number[]>([])
const popoverOpen = ref(false)

const selectedProjectsText = computed(() => {
  if (selectedProjectIds.value.length === 0) return 'Select projects...'
  if (selectedProjectIds.value.length === 1) {
    const project = projects.value.find(p => p.project_id === selectedProjectIds.value[0])
    return project ? `${project.project_name} (${project.organization_name})` : 'Select projects...'
  }
  return `${selectedProjectIds.value.length} projects selected`
})

const toggleProject = (projectId: number) => {
  const index = selectedProjectIds.value.indexOf(projectId)
  if (index > -1) {
    selectedProjectIds.value.splice(index, 1)
  } else {
    selectedProjectIds.value.push(projectId)
  }
}

const fetchProjects = async () => {
  isLoadingProjects.value = true
  try {
    // Fetch all projects from admin endpoint (doesn't require org membership)
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/projects`
    })

    projects.value = response.data
  } catch (error) {
    toast({
      title: 'Error',
      description: `Failed to fetch projects: ${error instanceof Error ? error.message : String(error)}`,
      variant: 'destructive'
    })
  } finally {
    isLoadingProjects.value = false
  }
}

const onSubmit = async () => {
  if (selectedProjectIds.value.length === 0) {
    toast({
      title: 'No projects selected',
      description: 'Please select at least one project',
      variant: 'destructive'
    })
    return
  }

  isProcessing.value = true

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/admin/templates/${props.templateId}/projects`,
      data: { project_ids: selectedProjectIds.value }
    })

    toast({
      title: `Template added to ${response.data.added_count} project(s)`,
    })

    emit('template-updated')
    close()
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to add template to projects'
    toast({
      title: errorMessage,
      variant: 'destructive'
    })
  } finally {
    isProcessing.value = false
  }
}

const close = () => {
  isOpen.value = false
  selectedProjectIds.value = []
  popoverOpen.value = false
}

onMounted(() => {
  fetchProjects()
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger as-child>
      <slot>
        <Button variant="default">Add to Projects</Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Add Template to Projects</DialogTitle>
        <DialogDescription>
          Select one or more projects to add this template to.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-4">
        <div class="space-y-2">
          <Label>Projects</Label>
          <Popover v-model:open="popoverOpen">
            <PopoverTrigger as-child>
              <Button
                variant="outline"
                role="combobox"
                :aria-expanded="popoverOpen"
                class="w-full justify-between"
                :disabled="isLoadingProjects"
              >
                <span class="truncate">{{ selectedProjectsText }}</span>
                <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-[460px] p-0">
              <Command>
                <CommandInput placeholder="Search projects..." />
                <CommandEmpty>No projects found.</CommandEmpty>
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      v-for="project in projects"
                      :key="project.project_id"
                      :value="`${project.project_name} ${project.organization_name}`"
                      @select="toggleProject(project.project_id)"
                    >
                      <Check
                        :class="cn(
                          'mr-2 h-4 w-4',
                          selectedProjectIds.includes(project.project_id) ? 'opacity-100' : 'opacity-0'
                        )"
                      />
                      <div class="flex-1 min-w-0">
                        <div class="truncate">{{ project.project_name }}</div>
                        <div class="text-xs text-gray-500 truncate">{{ project.organization_name }}</div>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div v-if="selectedProjectIds.length > 0" class="text-sm text-gray-600">
          Selected {{ selectedProjectIds.length }} project{{ selectedProjectIds.length !== 1 ? 's' : '' }}
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" type="button" @click="close">Cancel</Button>
        <Button
          type="button"
          @click="onSubmit"
          :disabled="isProcessing || selectedProjectIds.length === 0"
        >
          <Spinner v-if="isProcessing" class="w-4 h-4 mr-2" />
          <span v-else>Add to Projects</span>
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
